import { FC, useMemo } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import styles from "./DashboardTrackers.module.scss";
import { Checkbox, MultiSelect, Preloader } from "src/components";
import { selectTrackersById } from "src/store/selectors";
import { MultiSelectOption } from "src/components/selectors/MultiSelect/types";
import { TrackerChartIcon, TrackerKeywordsDataSourcesIcon } from "src/features";

type Props = {
  trackerIds: Tracker.Data["id"][];
  activeTrackerIds: Tracker.Data["id"][];
  loadingStatus: LoadingStatus;
  setActiveTrackerIdsHandler: (value: Tracker.Data["id"][]) => void;
};

export const DashboardTrackers: FC<Props> = ({
  trackerIds,
  activeTrackerIds,
  loadingStatus,
  setActiveTrackerIdsHandler,
}) => {
  const { t } = useTranslation();

  const trackers = useSelector((state: Store.RootState) =>
    selectTrackersById(state, trackerIds),
  );

  // Compute effective loading status - consider it loading if trackers haven't loaded yet
  const effectiveLoadingStatus = useMemo<LoadingStatus>(() => {
    // If formally loading, keep that status
    if (loadingStatus === "loading") return "loading";

    // If we have tracker IDs but no actual trackers, and status is succeeded, something is wrong
    if (
      trackerIds.length > 0 &&
      trackers.length === 0 &&
      loadingStatus === "succeeded"
    ) {
      return "loading"; // Treat as still loading
    }

    return loadingStatus;
  }, [loadingStatus, trackerIds, trackers]);

  const allTrackersLoaded = useMemo(() => {
    // If still loading, not loaded
    if (effectiveLoadingStatus === "loading") return false;
    // If loading failed, consider as loaded to allow user interaction (better UX than being stuck)
    if (effectiveLoadingStatus === "failed") return true;
    // If no trackers to load, consider it loaded
    if (trackerIds.length === 0) return true;
    // Must have at least one tracker loaded to consider it loaded
    return trackers.length > 0;
  }, [effectiveLoadingStatus, trackerIds, trackers]);

  const trackerOptions = useMemo<MultiSelectOption[]>(() => {
    if (!allTrackersLoaded || trackers.length === 0) return [];

    return trackers.map(({ name, id }) => ({
      value: id,
      label: name,
      renderLabel: () => (
        <div className={styles.trackerLabel}>
          <span>{name}</span>
          <TrackerKeywordsDataSourcesIcon trackerId={id} />
        </div>
      ),
      renderCheckbox: () => (
        <Checkbox
          isChecked={activeTrackerIds.includes(id)}
          elementChecked={
            <TrackerChartIcon
              trackerId={id}
              trackerIds={trackerIds}
              className={styles.trackerChartIcon}
            />
          }
          elementUnchecked={
            <TrackerChartIcon
              trackerId={id}
              trackerIds={trackerIds}
              className={styles.trackerChartIconInactive}
            />
          }
        />
      ),
    }));
  }, [activeTrackerIds, trackerIds, trackers, allTrackersLoaded]);

  const selectedTrackerOptions = useMemo<MultiSelectOption[]>(() => {
    if (!allTrackersLoaded || trackers.length === 0) return [];

    const selectedTrackerOptions = new Set<MultiSelectOption>();

    for (const id of activeTrackerIds) {
      const tracker = trackers.find((value) => value.id === id);

      if (!tracker) continue;

      selectedTrackerOptions.add({ value: id, label: tracker.name });
    }

    return [...selectedTrackerOptions];
  }, [activeTrackerIds, trackers, allTrackersLoaded]);

  const hasSingleTracker = useMemo<boolean>(
    () => trackerIds.length === 1,
    [trackerIds.length],
  );

  const customMultiselectInputText = useMemo(() => {
    if (effectiveLoadingStatus === "loading") {
      return t("page.dashboard.label.options_loading_trackers");
    }

    if (!allTrackersLoaded || trackers.length === 0) {
      if (trackerIds.length > 0) {
        return t("page.dashboard.label.options_loading_trackers");
      }
      return t("page.dashboard.label.options_no_trackers");
    }

    const [activeTrackerIdsLength, trackerIdsLength] = [
      activeTrackerIds.length,
      trackerIds.length,
    ];

    if (activeTrackerIdsLength === 1) {
      const tracker = trackers.find(
        (value) => value.id === activeTrackerIds[0],
      );

      if (!tracker) return t("page.dashboard.label.options_no_trackers");

      return tracker.name;
    }

    if (!trackerIdsLength) return t("page.dashboard.label.options_no_trackers");

    if (!activeTrackerIdsLength)
      return t("page.dashboard.label.options_select_trackers");

    if (activeTrackerIdsLength === trackerIdsLength)
      return t("page.dashboard.label.options_all_trackers_selected");

    return t("page.dashboard.label.options_trackers_selected", {
      count: activeTrackerIdsLength,
    });
  }, [
    activeTrackerIds,
    t,
    trackerIds.length,
    trackers,
    effectiveLoadingStatus,
    allTrackersLoaded,
  ]);

  const onSelectTrackerId = ({ value }: Option): void => {
    const isTrackerIdIncluded = activeTrackerIds.includes(value);

    const updatedActiveTrackerIds = isTrackerIdIncluded
      ? activeTrackerIds.filter((id) => id !== value)
      : [...activeTrackerIds, value];

    return setActiveTrackerIdsHandler(updatedActiveTrackerIds);
  };

  const onSelectAllTrackerIds = (): void => {
    const [activeTrackerIdsLength, trackerIdsLength] = [
      activeTrackerIds.length,
      trackerIds.length,
    ];

    const firstTrackerId = trackerIds[0];

    const updatedActiveTrackerIds =
      activeTrackerIdsLength === trackerIdsLength && firstTrackerId
        ? [firstTrackerId]
        : trackerIds;

    return setActiveTrackerIdsHandler(updatedActiveTrackerIds);
  };

  return (
    <div className={styles.wrapper}>
      {effectiveLoadingStatus === "loading" && (
        <div className={styles.loadingIndicator}>
          <Preloader type="bar" />
        </div>
      )}
      <MultiSelect
        icon="Filter"
        hasFilter={false}
        options={trackerOptions}
        inputClassName={styles.input}
        openingDirection="bottom-start"
        hasSelectAll={!hasSingleTracker}
        onCheckHandler={onSelectTrackerId}
        dropdownClassName={styles.dropdown}
        multiSelectClassName={styles.select}
        selectedOptions={selectedTrackerOptions}
        onSelectAllHandler={onSelectAllTrackerIds}
        customInputText={customMultiselectInputText}
        isDisabled={
          effectiveLoadingStatus === "loading" ||
          (trackers.length === 0 && trackerIds.length > 0)
        }
        placeholder={
          effectiveLoadingStatus === "loading"
            ? t("page.dashboard.label.options_loading_trackers")
            : t("page.dashboard.label.options_select_trackers")
        }
      />
    </div>
  );
};
